import React from 'react';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'success';
type Size = 'sm' | 'md' | 'lg';

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
};

const BASE_CLASS =
  'inline-flex items-center justify-center gap-1 border rounded-md font-hk-medium cursor-pointer whitespace-nowrap select-none transition-[background-color,border-color,box-shadow] duration-fast disabled:opacity-50 disabled:cursor-not-allowed';

const SIZE_CLASS: Record<Size, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-4 py-[7px] text-sm',
  lg: 'px-[22px] py-2.5 text-base',
};

// `enabled:` is the inverse of `disabled:` — applies hover/active only when the
// button isn't disabled, mirroring the original CSS `:hover:not(:disabled)`.
const VARIANT_CLASS: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--color-text-inverse)] border-[var(--color-primary)] ' +
    'enabled:hover:bg-[var(--color-primary-hover)] enabled:hover:border-[var(--color-primary-hover)] ' +
    'enabled:active:bg-[var(--color-primary-active)]',
  secondary:
    'bg-[var(--color-bg-white)] text-[var(--color-text-primary)] border-[var(--color-border-strong)] ' +
    'enabled:hover:bg-[var(--color-bg-hover)] enabled:hover:border-[var(--color-primary)] enabled:hover:text-[var(--color-primary)]',
  ghost:
    'bg-transparent text-[var(--color-text-secondary)] border-transparent ' +
    'enabled:hover:bg-[var(--color-bg-hover)] enabled:hover:text-[var(--color-text-primary)]',
  danger:
    'bg-[var(--color-danger)] text-[var(--color-text-inverse)] border-[var(--color-danger)] ' +
    'enabled:hover:opacity-90',
  'danger-outline':
    'bg-[var(--color-bg-white)] text-[var(--color-danger)] border-[var(--color-danger)] ' +
    'enabled:hover:bg-[var(--color-danger-light,#fdecea)]',
  success:
    'bg-[var(--color-success)] text-[var(--color-text-inverse)] border-[var(--color-success)] ' +
    'enabled:hover:bg-[var(--color-accent-hover)] enabled:hover:border-[var(--color-accent-hover)] ' +
    'enabled:active:bg-[var(--color-accent-active)] enabled:active:border-[var(--color-accent-active)]',
};

/**
 * Ref-forwarding is required, not cosmetic: `Popover` anchors its floating
 * panel by cloning its child and attaching a ref to read the trigger's DOM
 * rect. A plain function component drops that ref, so `<Popover><Button/></Popover>`
 * both logs "Function components cannot be given refs" AND positions the
 * panel against a null anchor.
 */
export const Button = React.forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    children,
    disabled,
    className,
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      disabled={disabled || loading}
      // A loading button is announced as busy, not merely as dimmed — the
      // spinner alone says nothing to assistive tech.
      aria-busy={loading}
      className={[BASE_CLASS, SIZE_CLASS[size], VARIANT_CLASS[variant], className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      {loading && (
        <span
          className="inline-block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
          aria-hidden="true"
        />
      )}
      {!loading && iconLeft && (
        <span className="inline-flex items-center leading-none">{iconLeft}</span>
      )}
      {children && <span>{children}</span>}
      {!loading && iconRight && (
        <span className="inline-flex items-center leading-none">{iconRight}</span>
      )}
    </button>
  );
});
